import { Box, Typography } from '@mui/material';
import React, { useContext } from 'react';
import ProfileInfo from 'src/common/components/ProfileInfo/ProfileInfo';
import { CustomMultiMediaView, LocationTag } from 'src/common/components';
import PrivatePost from '../PrivatePost/PrivatePost';
import { COLORS } from 'src/common/constants/constants';
import { GlobalContext } from 'src/common/contexts/globalContext';
import MarkedText from 'src/common/components/MarkedText/MarkedText';
import { Element } from 'react-scroll';
import { dateFormatter } from 'src/common/utils/formatters';
import MultiMarkedText from '../../../../../../../../../../../../common/components/MultiMarkedText/MultiMarkedText';
import { getKeywordsFromWatchLists, multiMarking } from 'src/common/utils/utils';

interface QuotedPostProps {
    post: any;
    section: string;
    extraImageContainerHeight?: number;
    imageContainerHeight?: number;
    readMore?: boolean;
}

const QuotedPost: React.FC<QuotedPostProps> = ({
    post,
    section,
    extraImageContainerHeight,
    imageContainerHeight,
    readMore,
}) => {
    const { reportData } = useContext(GlobalContext);
    const postDate = post.datePublished.datePublished;
    const height = extraImageContainerHeight ? imageContainerHeight + extraImageContainerHeight : imageContainerHeight;
    const getObject = (type) => {
        const obj = {};
        obj[`& mark:nth-of-type(${reportData.currentFindLink?.index + 1})`] =
            post.contentId === reportData.currentFindLink?.id &&
            type === reportData.currentFindLink?.type &&
            reportData.currentFindLink?.section === section
                ? { backgroundColor: `${COLORS.WARNING_LIGHTEST} !important` }
                : null;
        return obj;
    };
    const postKeywords = getKeywordsFromWatchLists(post.watchlist, reportData.currentHighlightsWatchlist?.id);
    return post.private ? (
        <PrivatePost
            post={post}
            sx={{
                height: height,
            }}
        />
    ) : (
        <Box
            sx={{
                height: height,
                borderRadius: '8px',
                overflow: 'hidden',
                width: '100%',
                border: `1px solid ${reportData.displayHighlights ? (post.contentId === reportData.currentHighlightsLink?.id ? COLORS.NEGATIVE : COLORS.MUTED_LIGHTEST) : post.contentId === reportData.currentFindLink?.id ? COLORS.WARNING_LIGHTEST : COLORS.MUTED_LIGHTEST}`,
            }}
        >
            {!readMore && <Element style={{ position: 'absolute' }} name={post.contentId + section}></Element>}
            <Box sx={{ p: 1, height: readMore ? null : postDate ? 144 : 120 }}>
                <ProfileInfo
                    imageSrc={post.author?.profileImageUrl}
                    topInfo={
                        reportData.displayHighlights ? (
                            <MultiMarkedText
                                containerProps={{
                                    variant: 's14w500',
                                    className: 'color-mh',
                                    sx: Object.assign(
                                        {
                                            '& mark': readMore
                                                ? {
                                                      backgroundColor: `${COLORS.NEGATIVE} !important`,
                                                      color: `${COLORS.LIGHT} !important`,
                                                  }
                                                : null,
                                        },
                                        multiMarking(
                                            post.contentId,
                                            section,
                                            'quotedPostName',
                                            reportData.currentHighlightsLink,
                                        ),
                                    ),
                                }}
                                searchTextArr={postKeywords}
                            >
                                {post.author?.name}
                            </MultiMarkedText>
                        ) : (
                            <MarkedText
                                containerProps={{
                                    variant: 's14w500',
                                    className: 'color-mh',
                                    sx: Object.assign(
                                        {
                                            '& mark': readMore
                                                ? { backgroundColor: `${COLORS.WARNING_LIGHTEST} !important` }
                                                : null,
                                        },
                                        getObject('quotedPostName'),
                                    ),
                                }}
                                searchText={reportData.find}
                            >
                                {post.author?.name}
                            </MarkedText>
                        )
                    }
                    bottomInfo={
                        reportData.displayHighlights ? (
                            <MultiMarkedText
                                containerProps={{
                                    variant: 's12w400',
                                    className: 'color-md',
                                    sx: Object.assign(
                                        {
                                            '& mark': readMore
                                                ? {
                                                      backgroundColor: `${COLORS.NEGATIVE} !important`,
                                                      color: `${COLORS.LIGHT} !important`,
                                                  }
                                                : null,
                                        },
                                        multiMarking(
                                            post.contentId,
                                            section,
                                            'quotedPostUsername',
                                            reportData.currentHighlightsLink,
                                        ),
                                    ),
                                }}
                                searchTextArr={postKeywords}
                            >
                                {post.author?.username}
                            </MultiMarkedText>
                        ) : (
                            <MarkedText
                                containerProps={{
                                    variant: 's12w400',
                                    className: 'color-md',
                                    sx: Object.assign(
                                        {
                                            '& mark': readMore
                                                ? { backgroundColor: `${COLORS.WARNING_LIGHTEST} !important` }
                                                : null,
                                        },
                                        getObject('quotedPostUsername'),
                                    ),
                                }}
                                searchText={reportData.find}
                            >
                                {post.author?.username}
                            </MarkedText>
                        )
                    }
                />
                {postDate && (
                    <Typography
                        sx={{
                            fontSize: '12px',
                            color: COLORS.MUTED_DARK,
                            cursor: 'pointer',
                            display: 'inline-flex',
                        }}
                    >
                        {dateFormatter(postDate)}
                    </Typography>
                )}
                {reportData.displayHighlights ? (
                    <MultiMarkedText
                        containerProps={{
                            title: post.body,
                            sx: Object.assign(
                                {
                                    mt: 1,
                                    display: readMore ? null : '-webkit-box',
                                    WebkitLineClamp: '2',
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    height: readMore ? null : '42px',
                                    whiteSpace: 'pre-line',
                                },
                                Object.assign(
                                    {
                                        '& mark': readMore
                                            ? {
                                                  backgroundColor: `${COLORS.NEGATIVE} !important`,
                                                  color: `${COLORS.LIGHT} !important`,
                                              }
                                            : null,
                                    },
                                    multiMarking(
                                        post.contentId,
                                        section,
                                        'quotedPostBody',
                                        reportData.currentHighlightsLink,
                                    ),
                                ),
                            ),
                        }}
                        searchTextArr={postKeywords}
                    >
                        {post.body}
                    </MultiMarkedText>
                ) : (
                    <MarkedText
                        containerProps={{
                            title: post.body,
                            sx: Object.assign(
                                {
                                    mt: 1,
                                    display: readMore ? null : '-webkit-box',
                                    WebkitLineClamp: '2',
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    height: readMore ? null : '42px',
                                    whiteSpace: 'pre-line',
                                },
                                Object.assign(
                                    {
                                        '& mark': readMore
                                            ? { backgroundColor: `${COLORS.WARNING_LIGHTEST} !important` }
                                            : null,
                                    },
                                    getObject('quotedPostBody'),
                                ),
                            ),
                        }}
                        searchText={reportData.find}
                    >
                        {post.body}
                    </MarkedText>
                )}
            </Box>
            <Box
                sx={{
                    height:
                        post.attachments?.length === 1
                            ? null
                            : extraImageContainerHeight
                              ? extraImageContainerHeight + 140
                              : 140,
                    width: post.attachments?.length === 1 ? '100%' : null,
                }}
            >
                {!!post.attachments?.length && (
                    <CustomMultiMediaView
                        media={post.attachments
                            .filter((attachment) => attachment.urls?.length)
                            .map((attachment: any) => ({
                                type: attachment.title,
                                url: attachment.urls[0],
                            }))}
                        roundedBottomCorners={true}
                        elementInImage={post.location?.address && <LocationTag address={post.location.address} />}
                    />
                )}
            </Box>
        </Box>
    );
};

export default QuotedPost;
